import type { QueryParams } from '~/types/api'
import type {
  SurfaceCollectionCategoryPagedResult,
  SurfaceCollectionPagedResult,
  SurfaceTypePagedResult,
  SurfacePagedResult,
} from '~/types/api'
import { addFilter, APIFilters } from '~/utils/APIFilters'

export function createSurfacesRepository() {
  const { $api } = useNuxtApp()
  const { get } = baseRepository($api)

  const DOMAIN_SURFACE_TYPES = 'surface-types'
  const DOMAIN_SURFACES = 'surfaces'
  const DOMAIN_COLLECTIONS = 'surface-collections'
  const DOMAIN_CATEGORIES = 'surface-collections-categories'
  const DOMAIN_PRODUCT_LINES = 'product-lines'
  const DOMAIN_PRODUCT_SETS = 'product-sets'
  const DOMAIN_PUBLIC_CATEGORIES = 'public-surface-collection-categories'
  const DOMAIN_PUBLIC_COLLECTIONS = 'public-surface-collections'

  const PRODUCTS = 'products'
  const SURFACES = 'surfaces'

  async function getAllSurfaceCollections(params?: QueryParams) {
    return get<SurfaceCollectionPagedResult>([DOMAIN_COLLECTIONS], {
      params,
    })
  }

  async function getAllSurfaceCollectionsCategories(params?: QueryParams) {
    return get<SurfaceCollectionCategoryPagedResult>([DOMAIN_CATEGORIES], {
      params,
    })
  }

  async function getAllSurfaceTypes(params?: QueryParams) {
    return get<SurfaceTypePagedResult>([DOMAIN_SURFACE_TYPES], { params })
  }

  async function getNonFabricAndMeshSurfaceTypes() {
    const filter = {
      [APIFilterOP.EQUALS]: {
        isFabricOrMesh: 0,
      },
    }
    const params = {
      filter: APIFilters.makeFilter(filter),
    }
    return get<SurfaceTypePagedResult>([DOMAIN_SURFACE_TYPES], { params })
  }

  async function getAllSurfaces(params?: QueryParams) {
    return get<SurfacePagedResult>([DOMAIN_SURFACES], { params })
  }

  async function getSetSurfacesByType(setNid: number, typeNid: number) {
    const filter = [addFilter('surfaceType.nid', typeNid)]
    addFilter('surfaceCollections.products.productSet.nid', setNid, filter)
    const params = {
      filter: APIFilters.makeFilter(filter),
    }
    return get<SurfacePagedResult>([DOMAIN_SURFACES], { params })
  }

  async function getLinesPublicSurfaceCategories(
    productLineId: number,
    params?: QueryParams
  ) {
    return get<SurfaceCollectionCategoryPagedResult>(
      [
        DOMAIN_PRODUCT_LINES,
        productLineId.toString(),
        DOMAIN_PUBLIC_CATEGORIES,
      ],
      { params }
    ) // TODO: langPath: ['base', PRODUCTS, SURFACES]
  }

  async function getLinesPublicSurfaceCollections(
    productLineId: number,
    params?: QueryParams
  ) {
    return get<SurfaceCollectionPagedResult>(
      [
        DOMAIN_PRODUCT_LINES,
        productLineId.toString(),
        DOMAIN_PUBLIC_COLLECTIONS,
      ],
      { params }
    ) // TODO: langPath: ['base', PRODUCTS, SURFACES],
  }

  async function getSetsPublicSurfaceCategories(
    productSetId: number,
    params?: QueryParams
  ) {
    return get<SurfaceCollectionCategoryPagedResult>(
      [DOMAIN_PRODUCT_SETS, productSetId.toString(), DOMAIN_PUBLIC_CATEGORIES],
      { params }
    ) // TODO: langPath: ['base', PRODUCTS, SURFACES]
  }

  async function getSetsPublicSurfaceCollections(
    productSetId: number,
    params?: QueryParams
  ) {
    return get<SurfaceCollectionPagedResult>(
      [DOMAIN_PRODUCT_SETS, productSetId.toString(), DOMAIN_PUBLIC_COLLECTIONS],
      { params }
    ) // TODO: langPath: ['base', PRODUCTS, SURFACES]
  }

  async function getSetsPublicSurfaceCollectionsByCategory(
    productSetId: number,
    categoryNid: number
  ) {
    const params = {
      filter: APIFilters.makeFilter([
        addFilter('surfaceCollectionCategory.nid', categoryNid),
      ]),
      sort: APIFilters.makeSort({ weight: 'ASC' }),
    }
    return get<SurfaceCollectionPagedResult>(
      [DOMAIN_PRODUCT_SETS, productSetId.toString(), DOMAIN_PUBLIC_COLLECTIONS],
      { params }
    ) // TODO: langPath: ['base', PRODUCTS, SURFACES]
  }

  return {
    getAllSurfaceCollections,
    getAllSurfaceCollectionsCategories,
    getAllSurfaceTypes,
    getNonFabricAndMeshSurfaceTypes,
    getAllSurfaces,
    getSetSurfacesByType,
    getLinesPublicSurfaceCategories,
    getLinesPublicSurfaceCollections,
    getSetsPublicSurfaceCategories,
    getSetsPublicSurfaceCollections,
    getSetsPublicSurfaceCollectionsByCategory,
  }
}
